"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/platform/auth/useAuth";
import { useUserEvaluations } from "../hooks/useSupplierEvaluation";
import { deleteEvaluation } from "@/platform/firebase/firestore/supplier-evaluation";
import { getStateLabelShort, type SupplierEvaluationProject } from "../types";

function stateColor(state: SupplierEvaluationProject["workflow_state"]): string {
  switch (state) {
    case "analyzed":           return "#10B981";
    case "scored":             return "#A855F7";
    case "responded":          return "#3B82F6";
    case "questionnaire_ready":return "#F59E0B";
    case "sent":               return "#6366F1";
    default:                   return "var(--ink-mute)";
  }
}

export function SupplierEvaluationHome() {
  const router  = useRouter();
  const { user } = useAuth();
  const { projects, loading, remove } = useUserEvaluations(user?.uid ?? null, user?.email ?? undefined);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const filtered = projects.filter((p) => {
    const q = search.toLowerCase();
    return !q ||
      p.project_name.toLowerCase().includes(q) ||
      p.supplier_info.company_name.toLowerCase().includes(q) ||
      p.supplier_info.email.toLowerCase().includes(q);
  });

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDeleting(id);
    try {
      await deleteEvaluation(id);
      remove(id);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <>
      {/* Hero */}
      <section className="diag-hero">
        <div className="diag-hero-icon" aria-hidden="true">
          <svg width="60" height="60" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <defs>
              <linearGradient id="se-hero-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#EC4899"/>
                <stop offset="100%" stopColor="#A855F7"/>
              </linearGradient>
            </defs>
            <path d="M9 11l2 2 4-4M21 12c0 5-3.5 8-9 8s-9-3-9-8 3.5-8 9-8 9 3 9 8z" stroke="url(#se-hero-grad)"/>
          </svg>
        </div>
        <h1><span className="grad-text">Supplier</span> Evaluation</h1>
        <p className="diag-hero-sub">AI-powered supplier assessment and development programme</p>
      </section>

      <div className="diag-page">
        {/* Toolbar */}
        <div style={{ display: "flex", gap: "12px", marginBottom: "24px", alignItems: "center" }}>
          <div className="search-box" style={{ flex: 1 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="search"
              placeholder="Search evaluations…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => router.push("/agentic-workflows/supplier-evaluation/new")}
            style={{
              background: "linear-gradient(135deg, var(--indigo), var(--magenta))",
              border: "none", borderRadius: "100px", padding: "9px 20px",
              fontSize: "14px", fontWeight: 600, color: "#fff", cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            + New Evaluation
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "var(--ink-mute)" }}>Loading…</div>
        ) : projects.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <p style={{ color: "var(--ink-mute)", marginBottom: "20px" }}>No evaluations yet</p>
            <button
              onClick={() => router.push("/agentic-workflows/supplier-evaluation/new")}
              style={{
                background: "linear-gradient(135deg, var(--indigo), var(--magenta))",
                border: "none", borderRadius: "100px", padding: "10px 24px",
                fontSize: "14px", fontWeight: 600, color: "#fff", cursor: "pointer",
              }}
            >
              Create Your First Evaluation
            </button>
          </div>
        ) : (
          <div className="diag-grid">
            {filtered.map((p) => (
              <article
                key={p.evaluation_id}
                className="diag-card d-srm"
                onClick={() => router.push(`/agentic-workflows/supplier-evaluation/${p.evaluation_id}`)}
                style={{ cursor: "pointer" }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ margin: "0 0 4px", fontSize: "16px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.project_name}
                    </h3>
                    <p style={{ fontSize: "13px", color: "var(--ink-mute)", margin: 0 }}>
                      {p.supplier_info.company_name} · {p.supplier_info.industry}
                    </p>
                    <p style={{ fontSize: "12px", color: "var(--ink-mute)", margin: "2px 0 0" }}>
                      {p.supplier_info.contact_person} · {p.supplier_info.email}
                    </p>
                  </div>
                  <span style={{
                    fontSize: "11px", fontWeight: 700, padding: "4px 10px",
                    borderRadius: "100px", whiteSpace: "nowrap", flexShrink: 0,
                    background: `${stateColor(p.workflow_state)}22`,
                    color: stateColor(p.workflow_state),
                    border: `1px solid ${stateColor(p.workflow_state)}44`,
                  }}>
                    {getStateLabelShort(p.workflow_state)}
                  </span>
                </div>

                <div className="card-foot" style={{ marginTop: "auto" }}>
                  <span className="card-time">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                    {new Date(p.updated_at).toLocaleDateString()}
                  </span>

                  {p.created_by_uid === user?.uid && (
                    <button
                      className="card-cta"
                      onClick={(e) => handleDelete(e, p.evaluation_id)}
                      disabled={deleting === p.evaluation_id}
                      style={{ borderColor: "rgba(239,68,68,0.3)" }}
                    >
                      {deleting === p.evaluation_id ? "…" : "Delete"}
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {filtered.length === 0 && projects.length > 0 && (
          <div className="diag-empty"><p>No evaluations match your search.</p></div>
        )}
      </div>
    </>
  );
}
